import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Package, Wrench, LogOut, Settings, BarChart3, Users, Image as ImageIcon, LayoutGrid } from 'lucide-react';
import AdminProducts from './dashboard/AdminProducts';
import OrdersList from './dashboard/OrdersList';
import ServicesList from './dashboard/ServicesList';
import StaffManagement from './dashboard/StaffManagement';
import Gallery from './dashboard/Gallery';
import CategoryManagement from './dashboard/CategoryManagement';
import CouponManagement from './dashboard/CouponManagement';
import ReviewsManagement from './dashboard/ReviewsManagement';
import PaymentSettings from './dashboard/PaymentSettings';
import { AlertCircle, Tag, MessageSquare, ExternalLink } from 'lucide-react';

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getMenuItems = () => {
    if (user?.role === 'admin' || user?.role === 'manager') {
      const items = [
        { to: '/dashboard', label: 'Overview', icon: BarChart3 },
        { to: '/dashboard/products', label: 'Products', icon: Package },
        { to: '/dashboard/categories', label: 'Categories', icon: LayoutGrid },
        { to: '/dashboard/orders', label: 'Orders', icon: Package },
        { to: '/dashboard/coupons', label: 'Coupons', icon: Tag },
        { to: '/dashboard/reviews', label: 'Reviews', icon: MessageSquare },
        { to: '/dashboard/services', label: 'Services', icon: Wrench },
        { to: '/dashboard/gallery', label: 'Gallery', icon: ImageIcon }
      ];
      
      // Only admins can manage staff and payment settings
      if (user?.role === 'admin') {
        items.push({ to: '/dashboard/staff', label: 'Staff & Users', icon: Users });
        items.push({ to: '/dashboard/settings/payment', label: 'Payment Settings', icon: Settings });
      }
      
      return items;
    } else if (user?.role === 'staff') {
      return [
        { to: '/dashboard', label: 'Overview', icon: BarChart3 },
        { to: '/dashboard/orders', label: 'Orders', icon: Package },
        { to: '/dashboard/services', label: 'Services', icon: Wrench },
        { to: '/dashboard/gallery', label: 'Gallery', icon: ImageIcon }
      ];
    } else if (user?.role === 'technician') {
      return [
        { to: '/dashboard', label: 'My Jobs', icon: Wrench },
        { to: '/dashboard/services', label: 'All Jobs', icon: Package }
      ];
    } else {
      return [
        { to: '/dashboard', label: 'Overview', icon: BarChart3 },
        { to: '/dashboard/orders', label: 'My Orders', icon: Package },
        { to: '/dashboard/services', label: 'My Services', icon: Wrench }
      ];
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="dashboard-layout">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-heading font-bold uppercase" data-testid="dashboard-title">
              {user?.role === 'admin' ? 'Admin' : 
               user?.role === 'manager' ? 'Manager' : 
               user?.role === 'staff' ? 'Staff' : 
               user?.role === 'technician' ? 'Technician' : 'Customer'} <span className="text-primary">Dashboard</span>
            </h1>
            <p className="text-slate-600">Welcome back, {user?.name}</p>
          </div>
          <div className="flex gap-2">
            <Link to="/">
              <Button variant="outline">
                Home
              </Button>
            </Link>
            <Button onClick={handleLogout} variant="outline" data-testid="dashboard-logout-button">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <Card className="lg:col-span-1 p-4 border-slate-200 h-fit" data-testid="dashboard-sidebar">
            <nav className="space-y-2">
              {getMenuItems().map(item => (
                <Link key={item.to} to={item.to} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <Button variant="ghost" className="w-full justify-start">
                    <item.icon className="h-4 w-4 mr-2" />
                    {item.label}
                  </Button>
                </Link>
              ))}
            </nav>
          </Card>

          <div className="lg:col-span-3">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

const DashboardHome = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [lowStock, setLowStock] = useState([]);

  useEffect(() => {
    if (['admin', 'manager'].includes(user?.role)) {
      fetchAnalytics();
      fetchLowStock();
    }
  }, [user]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics');
    }
  };

  const fetchLowStock = async () => {
    try {
      const response = await axios.get(`${API}/admin/low-stock?threshold=5`);
      setLowStock(response.data.products || []);
    } catch (error) {
      console.error('Failed to fetch low stock alerts');
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-8 border-slate-200" data-testid="dashboard-home">
        <h2 className="text-2xl font-heading font-bold mb-4">Welcome, {user?.name}!</h2>
      <p className="text-slate-600 mb-6">This is your dashboard. Use the menu to navigate to different sections.</p>
      
      {['admin', 'manager'].includes(user?.role) && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 bg-blue-50 border-blue-100">
            <h4 className="text-sm font-semibold text-blue-600 mb-1">Total Orders</h4>
            <p className="text-2xl font-bold text-blue-900">{stats.total_orders}</p>
          </Card>
          <Card className="p-4 bg-green-50 border-green-100">
            <h4 className="text-sm font-semibold text-green-600 mb-1">Total Revenue</h4>
            <p className="text-2xl font-bold text-green-900">₹{stats.total_revenue.toLocaleString()}</p>
          </Card>
          <Card className="p-4 bg-purple-50 border-purple-100">
            <h4 className="text-sm font-semibold text-purple-600 mb-1">Service Requests</h4>
            <p className="text-2xl font-bold text-purple-900">{stats.total_services}</p>
          </Card>
          <Card className="p-4 bg-orange-50 border-orange-100">
            <h4 className="text-sm font-semibold text-orange-600 mb-1">Low Stock Items</h4>
            <p className="text-2xl font-bold text-orange-900">{stats.low_stock_products}</p>
          </Card>
        </div>
      )}

      {user?.role === 'customer' && (
        <div className="space-y-4">
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <h3 className="font-semibold mb-2">Quick Actions</h3>
            <div className="flex gap-2">
              <Link to="/products">
                <Button className="bg-primary hover:bg-primary/90">Shop Products</Button>
              </Link>
              <Link to="/services">
                <Button variant="outline">Book Service</Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {user?.role === 'admin' && (
        <div className="space-y-4">
          <p className="text-slate-600">Manage your business operations from here:</p>
          <ul className="list-disc list-inside space-y-2 text-slate-700">
            <li>Add and manage products in the Products section</li>
            <li>Create and manage product categories</li>
            <li>View and process customer orders</li>
            <li>Assign technicians to service requests</li>
            <li>Monitor business performance and staff</li>
          </ul>
        </div>
      )}

      {user?.role === 'manager' && (
        <div className="space-y-4">
          <p className="text-slate-600">Oversight and management dashboard:</p>
          <ul className="list-disc list-inside space-y-2 text-slate-700">
            <li>Review and update products</li>
            <li>Manage orders and services</li>
            <li>Assign technicians to service requests</li>
            <li>Access gallery management</li>
          </ul>
        </div>
      )}

      {user?.role === 'staff' && (
        <div className="space-y-4">
          <p className="text-slate-600">Support and operations dashboard:</p>
          <ul className="list-disc list-inside space-y-2 text-slate-700">
            <li>View and update orders</li>
            <li>View and manage service requests</li>
            <li>Update gallery content</li>
          </ul>
        </div>
      )}

      {user?.role === 'technician' && (
        <div className="space-y-4">
          <p className="text-slate-600">View your assigned jobs and available service requests.</p>
        </div>
      )}
      </Card>

      {['admin', 'manager'].includes(user?.role) && lowStock.length > 0 && (
        <Card className="p-6 border-red-200 bg-red-50/50">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-red-700 flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Low Stock Alerts
            </h3>
            <Link to="/dashboard/products" className="text-sm font-medium text-red-600 hover:text-red-700 hover:underline">
              Manage Inventory →
            </Link>
          </div>
          <div className="space-y-3">
            {lowStock.map(product => (
              <div key={product.id} className="flex justify-between items-center bg-white p-3 rounded border border-red-100 shadow-sm">
                <div className="flex items-center gap-3">
                  {product.image_url ? (
                    <img src={product.image_url} alt={product.name} className="w-10 h-10 object-cover rounded" />
                  ) : (
                    <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
                      <Package className="h-5 w-5 text-slate-400" />
                    </div>
                  )}
                  <div>
                    <h4 className="font-semibold text-slate-800 text-sm">{product.name}</h4>
                    <p className="text-xs text-slate-500">ID: {product.id.substring(0, 8)}...</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    {product.stock} left in stock
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

const Dashboard = () => {
  return (
    <DashboardLayout>
      <Routes>
        <Route path="/" element={<DashboardHome />} />
        <Route path="/products" element={<AdminProducts />} />
        <Route path="/categories" element={<CategoryManagement />} />
        <Route path="/orders" element={<OrdersList />} />
        <Route path="/coupons" element={<CouponManagement />} />
        <Route path="/reviews" element={<ReviewsManagement />} />
        <Route path="/services" element={<ServicesList />} />
        <Route path="/staff" element={<StaffManagement />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/settings/payment" element={<PaymentSettings />} />
      </Routes>
    </DashboardLayout>
  );
};

export default Dashboard;
